import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { ACTIVITY_LOG_MAX_EVENTS, NOTIFICATIONS_MAX_COUNT } from "../constants/ui";

export interface ActivityEvent {
  id: string;
  type: "account" | "application" | "transaction" | "webhook" | "config" | "location";
  action: string;
  objectId: string;
  externalId: string;
  description: string;
  timestamp: Date;
  status: "success" | "warning" | "error" | "info";
}

export interface Notification {
  id: string;
  title: string;
  details: string;
  timestamp: Date;
  read: boolean;
}

interface UIState {
  showActivityLog: boolean;
  currentLocation: string;
  activityLogEvents: ActivityEvent[];
  activityLogFilter: string;
  selectedActivityEventId: string | null;
  notifications: Notification[];
  selectedNotificationId: string | null;
  isWebSocketEnabled: boolean;
  isWebSocketConnected: boolean;
  hasUnsavedChanges: boolean;
  setShowActivityLog: (show: boolean) => void;
  setCurrentLocation: (location: string) => void;
  addActivityLogEvent: (event: Omit<ActivityEvent, "id" | "timestamp">) => void;
  setActivityLogFilter: (filter: string) => void;
  setSelectedActivityEventId: (id: string | null) => void;
  addNotification: (notification: Omit<Notification, "id" | "timestamp" | "read">) => void;
  setSelectedNotificationId: (id: string | null) => void;
  setWebSocketEnabled: (enabled: boolean) => void;
  setWebSocketConnected: (connected: boolean) => void;
  markNotificationsAsRead: () => void;
  setUnsavedChanges: (hasChanges: boolean) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        showActivityLog: false,
        currentLocation: "default",
        activityLogEvents: [],
        activityLogFilter: "all",
        selectedActivityEventId: null,
        notifications: [],
        selectedNotificationId: null,
        isWebSocketEnabled: true,
        isWebSocketConnected: false,
        hasUnsavedChanges: false,
        setShowActivityLog: (show) => set({ showActivityLog: show }),
        setCurrentLocation: (location) => set({ currentLocation: location }),
        addActivityLogEvent: (event) =>
          set((state) => {
            const newEvent: ActivityEvent = {
              ...event,
              id: Math.random().toString(36).slice(2, 11),
              timestamp: new Date(),
            };

            const newNotification: Notification = {
              id: Math.random().toString(36).slice(2, 11),
              title: event.description,
              details: event.externalId,
              timestamp: newEvent.timestamp,
              read: false,
            };

            return {
              activityLogEvents: [
                newEvent,
                ...state.activityLogEvents.slice(0, ACTIVITY_LOG_MAX_EVENTS - 1),
              ],
              notifications: [
                newNotification,
                ...state.notifications.slice(0, NOTIFICATIONS_MAX_COUNT - 1),
              ],
            };
          }),
        setActivityLogFilter: (filter) => set({ activityLogFilter: filter }),
        setSelectedActivityEventId: (id) => set({ selectedActivityEventId: id }),
        addNotification: (notification) =>
          set((state) => {
            const newNotification: Notification = {
              ...notification,
              id: Math.random().toString(36).slice(2, 11),
              timestamp: new Date(),
              read: false,
            };
            return {
              notifications: [
                newNotification,
                ...state.notifications.slice(0, NOTIFICATIONS_MAX_COUNT - 1),
              ],
            };
          }),
        setSelectedNotificationId: (id) => set({ selectedNotificationId: id }),
        setWebSocketEnabled: (enabled) => set({ isWebSocketEnabled: enabled }),
        setWebSocketConnected: (connected) => set({ isWebSocketConnected: connected }),
        markNotificationsAsRead: () =>
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
          })),
        setUnsavedChanges: (hasChanges) => set({ hasUnsavedChanges: hasChanges }),
      }),
      {
        name: "ui-storage",
        partialize: (state) => ({
          currentLocation: state.currentLocation,
          isWebSocketEnabled: state.isWebSocketEnabled,
        }),
      }
    ),
    { name: "UIStore" }
  )
);
